import { useState } from 'react';
import { Play } from 'lucide-react';

/**
 * Fatada click-to-play pentru un videoclip YouTube, extrasa din
 * `VideoColectareBaterii.tsx` (cerut de ECOTIC, 2026-09-21) ca sa fie
 * reutilizabila pe orice ecran care are nevoie doar de un player simplu.
 *
 * Pana la apasare se randeaza DOAR miniatura (un JPEG de pe `i.ytimg.com`);
 * iframe-ul YouTube, cu cele cateva sute de KB de script ale lui, se incarca
 * abia la cerere — vezi ghidul de performanta din `modern-web-guidance`
 * (DON'T auto-play video files blindly). Dupa apasare, `autoplay=1` e un
 * raspuns la gestul omului, nu o surpriza.
 *
 * `youtube-nocookie.com`, ca in `VideoIntroPage.tsx`.
 *
 * `end`, in secunde, se transmite ca parametru al playerului embed — videoul
 * se opreste singur la acel timp (util pentru un extras dintr-un clip mai lung).
 *
 * ⚠️ Miniatura NU are `loading="lazy"` — apelantul decide daca sectiunea e
 * deasupra sau sub pliu; cutia `aspect-video` rezerva oricum spatiul inainte
 * sa soseasca imaginea (CLS 0).
 *
 * Nu foloseste `youtube-player.ts` (API-ul IFrame incarcat global) — acela
 * e pentru ecrane care trebuie sa STIE cand videoul s-a terminat
 * (`VideoIntroPage`). Aici e doar un player simplu, fara telemetrie de stare.
 */

export interface YoutubeFacadeProps {
    /** Id-ul de 11 caractere al videoclipului YouTube (nu URL-ul intreg). */
    videoId: string;
    /** Secunda la care se opreste playerul embed, daca e cazul. */
    end?: number;
    /** Titlul videoclipului — folosit si pentru `aria-label`, si pentru `iframe[title]`. */
    titlu: string;
}

export function YoutubeFacade({ videoId, end, titlu }: YoutubeFacadeProps) {
    const [porneste, setPorneste] = useState(false);

    const parametriEmbed = new URLSearchParams({ autoplay: '1', rel: '0', playsinline: '1' });
    if (end != null) parametriEmbed.set('end', String(end));

    return (
        <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-dark">
            {porneste ? (
                <iframe
                    src={`https://www.youtube-nocookie.com/embed/${videoId}?${parametriEmbed.toString()}`}
                    title={titlu}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full border-0"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setPorneste(true)}
                    aria-label={`Pornește videoclipul: ${titlu}`}
                    className="group absolute inset-0 flex h-full w-full cursor-pointer items-center justify-center border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white"
                >
                    <img
                        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                        alt=""
                        width={480}
                        height={360}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <span className="relative flex size-12 items-center justify-center rounded-full bg-magenta text-white shadow-md motion-safe:transition-transform motion-safe:group-hover:scale-110 motion-safe:group-focus-visible:scale-110">
                        <Play className="size-6 translate-x-px fill-current" aria-hidden />
                    </span>
                </button>
            )}
        </div>
    );
}
